import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { VerificationService } from "@/modules/verification/services/verificationService";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaCombobox, type ColocaComboboxOption } from "@/shared/components/forms/ColocaCombobox";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaStepper, type ColocaStepperStep } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { PARENT_CLIENT_LIST_LIMIT } from "../constants/clients.constants";
import { ClientsService } from "../services/clientsService";
import type { CreateClientBody, OrganizationTreeItem } from "../types/clients-api.types";
import { displayClientName } from "../utils/clientsTableHelpers";
import { rememberIssuedKycLink } from "../utils/issuedKycLinkCache";
import { displayNameFromCreateBody } from "../utils/normalizeClient";
import {
  BUSINESS_WIZARD_STEP_COUNT,
  BusinessClientForm,
  type BusinessWizardHandle,
} from "./create/BusinessClientForm";
import { ClientSuccessStep, type CreatedClientSummary } from "./create/ClientSuccessStep";
import { ClientTypeChoice, type ClientTypeKind } from "./create/ClientTypeChoice";
import type { IssuedKycLink } from "./create/OrganizationSuccessStep";
import { PersonClientForm } from "./create/PersonClientForm";

const PARENT_ORGANIZATION_SELECT_ID = "create-subclient-parent-organization";

const PERSON_FORM_ID = "create-subclient-person-form";
const BUSINESS_FORM_ID = "create-subclient-business-form";

/** Screens of the subclient drawer — the mock client branch without the entity choice. */
type SubclientScreen = "client_type" | "client_form";

export interface CreateSubclientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Preselects (and locks) the parent organization — used when opening from the drill-down view. */
  defaultOrganizationId?: string;
  /** Display name for the locked organization, in case it falls outside the fetched options page. */
  defaultOrganizationName?: string;
}

/**
 * Subclient create flow rendered as a side drawer (mock wizard container), org preselected/locked. Mirrors
 * the mock client branch: type selection through cards (`client_type`, footer Continue) → matching form →
 * success step sharing the engine KYC verification link (same screen as `CreateClientModal`).
 */
export function CreateSubclientModal({
  isOpen,
  onClose,
  onSuccess,
  defaultOrganizationId,
  defaultOrganizationName,
}: CreateSubclientModalProps) {
  const { t } = useTranslations("clients");
  const [screen, setScreen] = useState<SubclientScreen>("client_type");
  const [parentClientId, setParentClientId] = useState<string | null>(defaultOrganizationId ?? null);
  const [parentClientOptions, setParentClientOptions] = useState<ColocaComboboxOption[]>([]);
  const [parentClientsLoading, setParentClientsLoading] = useState(false);
  const [parentClientError, setParentClientError] = useState<string | null>(null);
  const [clientFormType, setClientFormType] = useState<ClientTypeKind | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessStep, setBusinessStep] = useState(0);
  const [createdClient, setCreatedClient] = useState<CreatedClientSummary | null>(null);
  const [createdKycLink, setCreatedKycLink] = useState<IssuedKycLink | null>(null);
  const businessWizardRef = useRef<BusinessWizardHandle>(null);

  const activeFormId = clientFormType === "business" ? BUSINESS_FORM_ID : PERSON_FORM_ID;
  const isBusinessIntermediateStep =
    screen === "client_form" &&
    clientFormType === "business" &&
    businessStep < BUSINESS_WIZARD_STEP_COUNT - 1;

  const fetchParentClients = useCallback(async () => {
    setParentClientsLoading(true);
    setParentClientError(null);
    try {
      const { data } = await ClientsService.fetchTree({ skip: 0, limit: PARENT_CLIENT_LIST_LIMIT });
      const options: ColocaComboboxOption[] = data.data.map((item: OrganizationTreeItem) => ({
        value: item.id,
        label: displayClientName(item),
      }));
      if (defaultOrganizationId && !options.some((option) => option.value === defaultOrganizationId)) {
        options.unshift({
          value: defaultOrganizationId,
          label: defaultOrganizationName ?? defaultOrganizationId,
        });
      }
      setParentClientOptions(options);
    } catch (err) {
      setParentClientError(HttpApiService.getErrorMessage(err));
      setParentClientOptions([]);
    } finally {
      setParentClientsLoading(false);
    }
  }, [defaultOrganizationId, defaultOrganizationName]);

  useEffect(() => {
    if (isOpen) fetchParentClients();
  }, [isOpen, fetchParentClients]);

  const resetForm = useCallback(() => {
    setScreen("client_type");
    setParentClientId(defaultOrganizationId ?? null);
    setClientFormType(null);
    setBusinessStep(0);
    setCreatedClient(null);
    setCreatedKycLink(null);
  }, [defaultOrganizationId]);

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen, resetForm]);

  const handleCreateSubclient = useCallback(
    async (body: CreateClientBody) => {
      // Implicit form submission (Enter) bypasses the disabled footer button — re-check the parent-org
      // requirement here so a client can never be posted without an organization.
      if (!parentClientId) return;
      setIsSubmitting(true);
      try {
        const { data } = await ClientsService.create(body);
        colocaToast.success(t("toast_create_client_success"));
        // Stay open on the success step so the engine verification link is visible right after create
        // (same screen as CreateClientModal). The create response has no identity joined, so the
        // display name comes from the submitted body (email/id as fallback).
        const summary: CreatedClientSummary = {
          id: data.id,
          identityId: data.identity_id ?? null,
          displayName: displayNameFromCreateBody(body) || data.email || data.id,
        };
        // Issue the KYC link through the verification engine — best-effort: the success step falls
        // back to the pending note when it fails.
        let kycLink: IssuedKycLink | null = null;
        if (summary.identityId) {
          try {
            const { data: link } = await VerificationService.issueLink(summary.identityId);
            kycLink = { url: link.verification_url, expiresAt: link.expires_at ?? null };
            // Remember it so the detail KYC section reuses this link instead of re-issuing (which
            // would expire it on the backend).
            rememberIssuedKycLink(summary.identityId, kycLink);
          } catch {
            // keep kycLink null
          }
        }
        setCreatedClient(summary);
        setCreatedKycLink(kycLink);
        onSuccess?.();
      } catch (err) {
        if (HttpApiService.isAxiosError(err) && err.response?.status === 409) {
          colocaToast.error(t("toast_create_client_duplicate"));
        } else {
          colocaToast.error(HttpApiService.getErrorMessage(err));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSuccess, t, parentClientId],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) onClose();
    },
    [onClose, isSubmitting],
  );

  // Back walks the business wizard first, then returns to the type selection.
  const handleBack = useCallback(() => {
    if (clientFormType === "business" && businessStep > 0) {
      businessWizardRef.current?.goBack();
      return;
    }
    setScreen("client_type");
  }, [clientFormType, businessStep]);

  const parentSelect = (
    <div className="grid gap-2">
      <ColocaLabel htmlFor={PARENT_ORGANIZATION_SELECT_ID}>
        {t("modal_label_parent_organization")}
      </ColocaLabel>
      <ColocaCombobox
        id={PARENT_ORGANIZATION_SELECT_ID}
        value={parentClientId}
        onValueChange={setParentClientId}
        options={parentClientOptions}
        placeholder={t("modal_placeholder_parent_organization")}
        disabled={isSubmitting || parentClientsLoading || Boolean(defaultOrganizationId)}
      />
    </div>
  );

  // Sticky drawer footer — same layout as the RBAC editor drawers (ml-auto action group on the right).
  const footer = createdClient ? (
    // Success step: the KYC link block carries its own copy action, so the footer only closes.
    <div className="flex w-full items-center gap-2">
      <div className="ml-auto flex items-center gap-2">
        <ColocaButton type="button" variant="primary" size="sm" onClick={onClose}>
          {t("org_success_done")}
        </ColocaButton>
      </div>
    </div>
  ) : (
    <div className="flex w-full items-center gap-2">
      <div className="ml-auto flex items-center gap-2">
        <ColocaButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => handleOpenChange(false)}
          disabled={isSubmitting}
        >
          {t("modal_cancel")}
        </ColocaButton>
        {screen === "client_form" && (
          <ColocaButton type="button" variant="ghost" size="sm" onClick={handleBack} disabled={isSubmitting}>
            {t("wizard_back")}
          </ColocaButton>
        )}
        {screen === "client_type" ? (
          <ColocaButton
            type="button"
            variant="primary"
            size="sm"
            iconTrailing={ArrowRight}
            // Mock `canContinue = !!identityType`.
            disabled={!clientFormType}
            onClick={() => setScreen("client_form")}
          >
            {t("wizard_continue")}
          </ColocaButton>
        ) : isBusinessIntermediateStep ? (
          <ColocaButton
            type="button"
            variant="primary"
            size="sm"
            iconTrailing={ArrowRight}
            onClick={() => void businessWizardRef.current?.goNext()}
            disabled={parentClientsLoading || !parentClientId}
          >
            {t("wizard_continue")}
          </ColocaButton>
        ) : (
          <ColocaButton
            type="submit"
            form={activeFormId}
            variant="primary"
            size="sm"
            disabled={isSubmitting || parentClientsLoading || !parentClientId}
            loading={isSubmitting}
          >
            {isSubmitting ? t("modal_creating") : t("modal_create_button")}
          </ColocaButton>
        )}
      </div>
    </div>
  );

  // Global progress stepper (mock client branch without the entity choice): [Identity, <form steps>,
  // Verify]. The flow always lands on the success/KYC screen, so that screen gets its own step.
  const stepperSteps: ColocaStepperStep[] = [{ label: t("wizard_step_identity") }];
  if (clientFormType === "business") {
    stepperSteps.push(
      { label: t("wizard_step_company") },
      { label: t("wizard_step_contact") },
      { label: t("wizard_step_owner") },
    );
  } else if (clientFormType === "individual") {
    stepperSteps.push({ label: t("wizard_step_details") });
  }
  stepperSteps.push({ label: t("wizard_step_verify") });
  // Defensive clamp — keeps the active index valid even if a transient state combination shrinks the list.
  const stepperCurrent = Math.min(
    createdClient
      ? stepperSteps.length - 1
      : screen === "client_type"
        ? 0
        : 1 + (clientFormType === "business" ? businessStep : 0),
    stepperSteps.length - 1,
  );

  // Mock drawer subtitles per screen (`titles[screen].s`).
  const description = createdClient
    ? t("client_success_subtitle")
    : screen === "client_type"
      ? t("drawer_client_description")
      : clientFormType === "business"
        ? businessStep === 2
          ? t("wizard_step_owner")
          : businessStep === 1
            ? t("wizard_step_contact")
            : t("wizard_step_company")
        : t("drawer_client_individual_description");

  return (
    <ColocaDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      title={createdClient ? t("client_success_title") : t("modal_create_title")}
      description={description}
      footer={footer}
    >
      <div className="space-y-4">
        <ColocaStepper
          steps={stepperSteps}
          currentStep={stepperCurrent}
          aria-label={t("wizard_progress_aria")}
          className="border-b border-border pb-4"
        />
        {createdClient && <ClientSuccessStep client={createdClient} kycLink={createdKycLink} />}
        {!createdClient && screen === "client_type" && (
          <ClientTypeChoice value={clientFormType} onChange={setClientFormType} />
        )}

        {/* The form branch stays mounted (hidden) so captured values survive Back navigation. */}
        <div className={screen !== "client_form" || createdClient ? "hidden" : "space-y-4"}>
          {parentClientError && (
            <div className="rounded-lg bg-warning-container p-3 text-sm text-warning-container-foreground">
              {parentClientError}
            </div>
          )}

          {parentSelect}

          <div className={clientFormType === "business" ? "hidden" : undefined}>
            <PersonClientForm
              formId={PERSON_FORM_ID}
              onSubmit={handleCreateSubclient}
              organizationId={parentClientId ?? undefined}
            />
          </div>
          <div className={clientFormType !== "business" ? "hidden" : undefined}>
            <BusinessClientForm
              ref={businessWizardRef}
              formId={BUSINESS_FORM_ID}
              onSubmit={handleCreateSubclient}
              organizationId={parentClientId ?? undefined}
              onStepChange={setBusinessStep}
            />
          </div>
        </div>
      </div>
      {/* Sentinel: with a single body child, ColocaDrawer sticky-pins its last child (a helper meant for
          footer-less wizards). We render our actions in the `footer` prop, so force the normal flow. */}
      <span aria-hidden className="hidden" />
    </ColocaDrawer>
  );
}
